import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Flag, RefreshCw, Plus, Trash2, Wrench, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Loading, Banner } from '@/components/OpsUI';

export default function AppFlagsPage() {
  const { t } = useTranslation();
  const [flags, setFlags] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newFlag, setNewFlag] = useState({ key: '', value: 'true', description: '' });
  const [thr, setThr] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/ops/flags');
      setFlags(data);
    } catch (e) { setError(errText(e)); }
    try {
      const { data } = await api.get('/ops/alerts');
      setAlerts(data);
      setThr({
        error_5xx: { ...data.error_5xx },
        payment_backlog: { ...data.payment_backlog },
      });
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const maintItem = (flags?.items || []).find((f) => f.key === 'maintenance_mode');
  const maintenanceOn = !!maintItem?.value;

  const setFlagValue = async (key, value, description) => {
    setSaving(true);
    try {
      await api.put(`/ops/flags/${key}`, { value, description });
      toast.success(`Flag '${key}' actualizado`);
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const toggleMaintenance = async (checked) => {
    await setFlagValue('maintenance_mode', checked, maintItem?.description || 'Modo mantenimiento');
  };

  const deleteFlag = async (key) => {
    if (key === 'maintenance_mode') { toast.error('No elimines maintenance_mode; usa el toggle.'); return; }
    setSaving(true);
    try { await api.delete(`/ops/flags/${key}`); toast.success(`Flag '${key}' eliminado`); load(); }
    catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const createFlag = async () => {
    if (!newFlag.key.trim()) return;
    let value = newFlag.value;
    try { value = JSON.parse(newFlag.value); } catch { /* keep as string */ }
    await setFlagValue(newFlag.key.trim(), value, newFlag.description);
    setNewFlag({ key: '', value: 'true', description: '' });
  };

  const saveThresholds = async () => {
    if (!thr) return;
    setSaving(true);
    try {
      const body = {
        error_5xx: {
          enabled: !!thr.error_5xx.enabled,
          window_minutes: Number(thr.error_5xx.window_minutes),
          max_count: Number(thr.error_5xx.max_count),
          cooldown_minutes: Number(thr.error_5xx.cooldown_minutes),
        },
        payment_backlog: {
          enabled: !!thr.payment_backlog.enabled,
          max_count: Number(thr.payment_backlog.max_count),
          stuck_minutes: Number(thr.payment_backlog.stuck_minutes),
          cooldown_minutes: Number(thr.payment_backlog.cooldown_minutes),
        },
      };
      await api.put('/ops/alerts', body);
      toast.success(t('ops_thresholds') + ' ✓');
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const upThr = (group, field, val) => setThr((p) => ({ ...p, [group]: { ...p[group], [field]: val } }));

  return (
    <div className="space-y-6" data-testid="app-flags-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Flag className="h-6 w-6 text-[hsl(var(--primary))]" /> {t('nav_app_flags')}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Control de la App en vivo</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="app-flags-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t('refresh')}
        </Button>
      </div>

      {error && <Banner kind="error" message={String(error)} />}

      {loading ? <Loading label={t('loading')} /> : (
        <>
          {/* Maintenance mode */}
          <Card className={`p-5 border ${maintenanceOn ? 'border-amber-500/40 bg-amber-500/10' : 'border-border bg-card'}`} data-testid="ops-maintenance-card">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Wrench className={`h-6 w-6 mt-0.5 ${maintenanceOn ? 'text-amber-400' : 'text-[hsl(var(--primary))]'}`} />
                <div>
                  <div className="font-display font-semibold">{t('ops_maintenance_mode')}</div>
                  <div className={`text-sm mt-0.5 ${maintenanceOn ? 'text-amber-300' : 'text-[hsl(var(--muted-foreground))]'}`}>
                    {maintenanceOn ? t('ops_maintenance_on') : t('ops_maintenance_off')}
                  </div>
                </div>
              </div>
              <Switch checked={maintenanceOn} disabled={saving} onCheckedChange={toggleMaintenance} data-testid="ops-maintenance-switch" />
            </div>
            {maintenanceOn && (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-300">
                <AlertTriangle className="h-4 w-4" /> La App responde 503 a los usuarios (excepto health/auth/ops).
              </div>
            )}
          </Card>

          {/* Thresholds */}
          {thr && (
            <Card className="p-5 bg-card border border-border">
              <div className="text-sm font-display font-semibold mb-4">{t('ops_thresholds')}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">{t('ops_error_rate')}</div>
                    <Switch checked={!!thr.error_5xx.enabled} onCheckedChange={(v) => upThr('error_5xx', 'enabled', v)} data-testid="ops-thr-error-enabled" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px]">{t('ops_window_min')}</Label><Input type="number" value={thr.error_5xx.window_minutes} onChange={(e) => upThr('error_5xx', 'window_minutes', e.target.value)} /></div>
                    <div><Label className="text-[10px]">{t('ops_max_count')}</Label><Input type="number" value={thr.error_5xx.max_count} onChange={(e) => upThr('error_5xx', 'max_count', e.target.value)} /></div>
                    <div><Label className="text-[10px]">{t('ops_cooldown_min')}</Label><Input type="number" value={thr.error_5xx.cooldown_minutes} onChange={(e) => upThr('error_5xx', 'cooldown_minutes', e.target.value)} /></div>
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('ops_current')}: {alerts?.error_5xx?.current ?? '—'} {alerts?.error_5xx?.breaching && <span className="text-red-300">· {t('ops_breaching')}</span>}</div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">{t('ops_payment_backlog')}</div>
                    <Switch checked={!!thr.payment_backlog.enabled} onCheckedChange={(v) => upThr('payment_backlog', 'enabled', v)} data-testid="ops-thr-backlog-enabled" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px]">{t('ops_max_count')}</Label><Input type="number" value={thr.payment_backlog.max_count} onChange={(e) => upThr('payment_backlog', 'max_count', e.target.value)} /></div>
                    <div><Label className="text-[10px]">{t('ops_stuck_min')}</Label><Input type="number" value={thr.payment_backlog.stuck_minutes} onChange={(e) => upThr('payment_backlog', 'stuck_minutes', e.target.value)} /></div>
                    <div><Label className="text-[10px]">{t('ops_cooldown_min')}</Label><Input type="number" value={thr.payment_backlog.cooldown_minutes} onChange={(e) => upThr('payment_backlog', 'cooldown_minutes', e.target.value)} /></div>
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('ops_current')}: {alerts?.payment_backlog?.current ?? '—'} {alerts?.payment_backlog?.breaching && <span className="text-red-300">· {t('ops_breaching')}</span>}</div>
                </div>
              </div>
              <div className="mt-4">
                <Button size="sm" disabled={saving} onClick={saveThresholds} data-testid="ops-thr-save">{t('save')}</Button>
              </div>
            </Card>
          )}

          {/* Flags list */}
          <Card className="bg-card border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="text-sm font-display font-semibold">{t('ops_flags_title')} ({flags?.count ?? 0})</div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="ops-new-flag-btn"><Plus className="h-3.5 w-3.5 mr-1" /> {t('ops_new_flag')}</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t('ops_new_flag')}</DialogTitle><DialogDescription>Define una clave, su valor (JSON) y una descripción opcional.</DialogDescription></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>{t('ops_flag_key')}</Label><Input value={newFlag.key} onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value })} placeholder="ej: new_checkout" data-testid="ops-flag-key-input" /></div>
                    <div><Label>{t('ops_flag_value')}</Label><Input value={newFlag.value} onChange={(e) => setNewFlag({ ...newFlag, value: e.target.value })} placeholder='true | false | "texto" | 10' data-testid="ops-flag-value-input" /></div>
                    <div><Label>{t('ops_flag_desc')}</Label><Input value={newFlag.description} onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })} data-testid="ops-flag-desc-input" /></div>
                  </div>
                  <DialogFooter>
                    <Button disabled={saving || !newFlag.key.trim()} onClick={createFlag} data-testid="ops-flag-create-btn">{t('create')}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="max-h-[380px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                    <th className="px-4 py-3">{t('ops_flag_key')}</th><th className="px-4 py-3">{t('ops_flag_value')}</th><th className="px-4 py-3">{t('ops_flag_desc')}</th><th className="px-4 py-3">updated_by</th><th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {(flags?.items || []).map((f) => (
                    <tr key={f.key} className="border-b border-border/40 hover:bg-accent/40" data-testid={`ops-flag-row-${f.key}`}>
                      <td className="px-4 py-3 font-mono text-xs">{f.key}</td>
                      <td className="px-4 py-3 font-mono text-xs">{JSON.stringify(f.value)}</td>
                      <td className="px-4 py-3 text-xs text-[hsl(var(--muted-foreground))]">{f.description || '—'}</td>
                      <td className="px-4 py-3 text-xs">{f.updated_by || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" disabled={saving || f.key === 'maintenance_mode'} onClick={() => deleteFlag(f.key)} data-testid={`ops-flag-delete-${f.key}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {(!flags?.items || flags.items.length === 0) && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
